import React from "react";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSelector from "./LanguageSelector";
import { ArrowRightIcon, UploadIcon } from "./SvgIcons";

interface LandingIntroProps {
  authCard: React.ReactNode;
  authCardRef: React.RefObject<HTMLDivElement | null>;
  onStartReading: () => void;
}

const LevelIllustration = () => (
  <div
    aria-hidden="true"
    className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-5"
  >
    <div className="grid grid-cols-3 gap-2">
      {["A1", "A2", "B1", "B2", "C1", "C2"].map((level) => (
        <span
          key={level}
          className={
            level === "B1"
              ? "rounded-lg bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm"
              : "rounded-lg bg-white px-3 py-2 text-center text-sm font-medium text-gray-600"
          }
        >
          {level}
        </span>
      ))}
    </div>
  </div>
);

const AddTextIllustration = ({ pasteLabel }: { pasteLabel: string }) => (
  <div
    aria-hidden="true"
    className="rounded-2xl border border-purple-100 bg-purple-50/70 p-5"
  >
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium text-purple-700">
        <UploadIcon className="h-5 w-5" />
        <span>{pasteLabel}</span>
      </div>
      <div className="space-y-2">
        <div className="h-2 rounded bg-gray-200" />
        <div className="h-2 w-5/6 rounded bg-gray-200" />
        <div className="h-2 w-2/3 rounded bg-gray-200" />
      </div>
      <div className="mt-4 rounded-lg border border-dashed border-purple-300 px-3 py-2 text-center text-xs font-semibold text-purple-700">
        TXT / EPUB
      </div>
    </div>
  </div>
);

const ReadingIllustration = ({
  definitionLabel,
  saveLabel,
}: {
  definitionLabel: string;
  saveLabel: string;
}) => (
  <div
    aria-hidden="true"
    className="rounded-2xl border border-pink-100 bg-pink-50/70 p-5"
  >
    <div className="rounded-xl bg-white p-4 shadow-sm">
      <div className="space-y-2">
        <div className="h-2 rounded bg-gray-200" />
        <div className="flex items-center gap-2">
          <div className="h-2 w-1/3 rounded bg-gray-200" />
          <div className="h-5 w-20 rounded bg-amber-200" />
          <div className="h-2 flex-1 rounded bg-gray-200" />
        </div>
        <div className="h-2 w-4/5 rounded bg-gray-200" />
      </div>
      <div className="mt-4 rounded-lg border border-pink-100 bg-pink-50 p-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-pink-700">
          {definitionLabel}
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="h-2 w-2/3 rounded bg-pink-200" />
          <span className="rounded-full bg-pink-600 px-2 py-1 text-xs font-semibold text-white">
            {saveLabel}
          </span>
        </div>
      </div>
    </div>
  </div>
);

const LandingIntro: React.FC<LandingIntroProps> = ({
  authCard,
  authCardRef,
  onStartReading,
}) => {
  const appIconSrc = `${process.env.PUBLIC_URL}/logo192.png`;
  const { t } = useLanguage();
  const workflowSteps = [
    {
      title: t("login.workflow.levelTitle"),
      body: t("login.workflow.levelBody"),
      illustration: <LevelIllustration />,
    },
    {
      title: t("login.workflow.addTitle"),
      body: t("login.workflow.addBody"),
      illustration: (
        <AddTextIllustration pasteLabel={t("login.workflow.pasteLabel")} />
      ),
    },
    {
      title: t("login.workflow.readTitle"),
      body: t("login.workflow.readBody"),
      illustration: (
        <ReadingIllustration
          definitionLabel={t("login.workflow.definitionLabel")}
          saveLabel={t("login.workflow.saveLabel")}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 px-4 py-6 text-gray-900 sm:px-6 lg:px-8">
      <header className="mx-auto flex max-w-6xl items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={appIconSrc} alt="" className="h-11 w-11 rounded-xl" />
          <span className="text-xl font-bold">{t("common.appName")}</span>
        </div>
        <LanguageSelector />
      </header>

      <main className="mx-auto max-w-6xl">
        <section className="grid min-h-[calc(100vh-5rem)] items-center gap-10 py-10 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div>
            <h1 className="text-4xl font-bold leading-tight text-gray-950 sm:text-5xl">
              {t("login.heroTitle")}
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-gray-700 sm:text-lg">
              {t("login.heroBody")}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-4">
              <button
                type="button"
                onClick={onStartReading}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                {t("login.startReading")}
                <ArrowRightIcon className="h-5 w-5" />
              </button>
              <a
                href="#how-it-works"
                className="inline-flex min-h-11 items-center rounded-lg px-2 py-3 font-semibold text-purple-700 underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
              >
                {t("login.seeHowItWorks")}
              </a>
            </div>
          </div>

          <div id="auth-card" ref={authCardRef} className="scroll-mt-6">
            {authCard}
          </div>
        </section>

        <section
          id="how-it-works"
          className="scroll-mt-6 py-14"
          aria-labelledby="how-it-works-title"
        >
          <div className="mx-auto max-w-2xl text-center">
            <h2
              id="how-it-works-title"
              className="text-3xl font-bold text-gray-950 sm:text-4xl"
            >
              {t("login.howItWorksTitle")}
            </h2>
            <p className="mt-3 text-base leading-7 text-gray-600">
              {t("login.howItWorksBody")}
            </p>
          </div>

          <ol
            aria-label={t("login.howItWorksTitle")}
            className="mt-10 grid gap-6 lg:grid-cols-3"
          >
            {workflowSteps.map((step, index) => (
              <li
                key={step.title}
                className="rounded-2xl border border-white/70 bg-white/80 p-5 shadow-lg shadow-purple-100/50"
              >
                {step.illustration}
                <div className="mt-5 flex gap-3">
                  <span
                    aria-hidden="true"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white"
                  >
                    {index + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-gray-950">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm leading-6 text-gray-600">
                      {step.body}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mx-auto mt-10 max-w-2xl text-center">
            <p className="rounded-lg border border-purple-100 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm">
              {t("login.privacyNote")}
            </p>
            <button
              type="button"
              onClick={onStartReading}
              className="mt-6 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-purple-600 px-5 py-3 font-semibold text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              {t("login.startReading")}
              <ArrowRightIcon className="h-5 w-5" />
            </button>
            <nav
              aria-label={t("login.exploreLabel")}
              className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm font-medium text-purple-700"
            >
              <a href="/simplify-english-text">
                {t("login.exploreTextSimplifier")}
              </a>
              <a href="/english-epub-reader">{t("login.exploreEpubReader")}</a>
              <a href="/zh/english-reading-tool">
                {t("login.exploreChineseLearners")}
              </a>
            </nav>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandingIntro;
